<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useDisplay } from 'vuetify'
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import CustomCloseButton from '@/components/createdUi/CustomCloseButton.vue'
import LocationTypeModal from '@/modules/settings/components/LocationTypeModal.vue'
import { getApiError } from '@/composables/getApiError'
import { useFeedbackStore } from '@/modules/feedback/stores/feedbackStore'
import { locationTypesApi } from '@/modules/settings/services/locationTypesApi'
import type { HospitalLocationType } from '@/modules/settings/types/hospitalStructure.types'

interface Props {
  isOpen: boolean
}

interface Emits {
  (e: 'update:isOpen', value: boolean): void
  (e: 'changed'): void
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

const { smAndDown } = useDisplay()
const feedbackStore = useFeedbackStore()

const types = ref<HospitalLocationType[]>([])
const isLoading = ref(false)
const search = ref('')
const isFormOpen = ref(false)
const editingType = ref<HospitalLocationType | null>(null)
const isDeleteConfirmOpen = ref(false)
const typeToDelete = ref<HospitalLocationType | null>(null)
const isDeleting = ref(false)

const filteredTypes = computed(() => {
  const query = search.value.trim().toLowerCase()
  if (!query) {
    return types.value
  }

  return types.value.filter(type => {
    const haystack = [type.name, type.description].filter(Boolean).join(' ').toLowerCase()
    return haystack.includes(query)
  })
})

async function loadTypes() {
  isLoading.value = true
  try {
    types.value = await locationTypesApi.list(
      search.value.trim() ? { search: search.value.trim() } : undefined,
    )
  } catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(caught, 'Не удалось загрузить типы помещений'),
    })
  } finally {
    isLoading.value = false
  }
}

watch(
  () => props.isOpen,
  async open => {
    if (!open) {
      search.value = ''
      return
    }
    await loadTypes()
  },
)

function closeModal() {
  emit('update:isOpen', false)
}

function openCreate() {
  editingType.value = null
  isFormOpen.value = true
}

function openEdit(item: HospitalLocationType) {
  editingType.value = item
  isFormOpen.value = true
}

async function onTypeSaved() {
  await loadTypes()
  emit('changed')
}

function requestDelete(item: HospitalLocationType) {
  typeToDelete.value = item
  isDeleteConfirmOpen.value = true
}

async function handleDeleteConfirm(confirmed: boolean) {
  isDeleteConfirmOpen.value = false
  if (!confirmed || !typeToDelete.value) {
    typeToDelete.value = null
    return
  }

  isDeleting.value = true
  try {
    await locationTypesApi.remove(typeToDelete.value.id)
    feedbackStore.showToast({
      type: 'success',
      message: 'Тип помещения удалён',
    })
    await loadTypes()
    emit('changed')
  } catch (caught) {
    feedbackStore.showToast({
      type: 'error',
      message: getApiError(caught, 'Не удалось удалить тип помещения'),
    })
  } finally {
    isDeleting.value = false
    typeToDelete.value = null
  }
}
</script>

<template>
  <VDialog
    :model-value="isOpen"
    :max-width="smAndDown ? '100%' : 720"
    :fullscreen="smAndDown"
    persistent
    @update:model-value="emit('update:isOpen', $event)"
  >
    <VCard :class="smAndDown ? 'pa-4' : 'pa-6'">
      <div class="d-flex justify-space-between align-center mb-4">
        <span class="text-h5 font-weight-bold">Типы помещений</span>
        <CustomCloseButton :close-action="closeModal" />
      </div>

      <div
        class="d-flex align-center ga-3 mb-4"
        :class="{ 'flex-column align-stretch': smAndDown }"
      >
        <VTextField
          v-model="search"
          variant="outlined"
          density="compact"
          prepend-inner-icon="ri-search-line"
          placeholder="Поиск"
          hide-details
          clearable
          class="flex-grow-1"
          @keyup.enter="loadTypes"
          @click:clear="loadTypes"
        />
        <VBtn
          color="primary"
          prepend-icon="ri-add-line"
          :block="smAndDown"
          @click="openCreate"
        >
          Добавить
        </VBtn>
      </div>

      <VProgressLinear
        v-if="isLoading"
        indeterminate
        color="primary"
        class="mb-2"
      />

      <VList v-if="filteredTypes.length" class="pa-0 types-list">
        <VListItem
          v-for="item in filteredTypes"
          :key="item.id"
          class="px-2 rounded-md"
        >
          <VListItemTitle class="text-body-1 d-flex align-center flex-wrap ga-2">
            <span>{{ item.name }}</span>
            <VChip
              v-if="item.can_have_address"
              size="x-small"
              color="primary"
              variant="tonal"
            >
              Может иметь адрес
            </VChip>
          </VListItemTitle>
          <VListItemSubtitle v-if="item.description" class="text-medium-emphasis">
            {{ item.description }}
          </VListItemSubtitle>
          <template #append>
            <div class="d-flex ga-1">
              <VBtn
                icon="ri-edit-line"
                variant="text"
                color="secondary"
                size="small"
                aria-label="Редактировать"
                @click="openEdit(item)"
              />
              <VBtn
                icon="ri-delete-bin-line"
                variant="text"
                color="error"
                size="small"
                :loading="isDeleting && typeToDelete?.id === item.id"
                aria-label="Удалить"
                @click="requestDelete(item)"
              />
            </div>
          </template>
        </VListItem>
      </VList>

      <div
        v-else-if="!isLoading"
        class="d-flex flex-column align-center justify-center ga-2 pa-8 text-medium-emphasis"
      >
        <VIcon icon="ri-building-line" size="40" />
        <div class="text-body-1">
          Типы помещений не найдены
        </div>
      </div>
    </VCard>
  </VDialog>

  <LocationTypeModal
    v-model:is-open="isFormOpen"
    :location-type="editingType"
    @saved="onTypeSaved"
  />

  <ConfirmDialog
    v-model:is-dialog-visible="isDeleteConfirmOpen"
    confirmation-question="Удалить тип помещения?"
    :confirmation-text="typeToDelete ? `«${typeToDelete.name}» будет удалён.` : ''"
    confirm-btn="Удалить"
    reject-btn="Отмена"
    @confirm="handleDeleteConfirm"
  />
</template>

<style scoped lang="scss">
.types-list {
  max-height: min(60vh, 480px);
  overflow-y: auto;
}
</style>
